import { useInputControl } from '@conform-to/react'
import { animated, useSpring } from '@react-spring/web'
import { format } from 'date-fns'
import { type OTPInputProps, REGEXP_ONLY_DIGITS_AND_CHARS } from 'input-otp'
import { ChevronDown, Search, TriangleAlert } from 'lucide-react'
import type React from 'react'
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import GooglePlacesAutocomplete from 'react-google-places-autocomplete'
import closeEye from '/img/close-eye-icon.png'
import openEye from '/img/open-eye-icon.png'
import { gentleSpring, smoothSpring } from '#app/utils/animations'
import { cn, sanitizeMoneyInput } from '#app/utils/misc'
import { Checkbox, type CheckboxProps } from './ui/checkbox'
import { Input } from './ui/input'
import { InputOTP, InputOTPSlot } from './ui/input-otp'
import { Label } from './ui/label'
import { PhoneInput } from './ui/phone-input'
import { Textarea } from './ui/textarea'

export type ListOfErrors = Array<string | null | undefined> | null | undefined

function formatCurrencyAmountDisplay(value: string | number): string {
	const raw = sanitizeMoneyInput(String(value ?? ''))
	if (!raw) return ''
	return raw.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

/** True when `errors` holds a real (non-`warning:`) error. Warnings are advisory
 * (e.g. an out-of-range amount) and must NOT mark the field invalid — otherwise
 * the aria-[invalid] red border sticks even though the entered value is fine. */
function hasBlockingError(errors: ListOfErrors): boolean {
	return Boolean(
		errors?.some(
			(e) => e != null && !(typeof e === 'string' && e.startsWith('warning:')),
		),
	)
}

export function ErrorList({
	id,
	errors,
	hideWarnings = false,
	hideErrors = false,
	className,
}: {
	errors?: ListOfErrors
	id?: string
	/** When true, suppress `warning:`-prefixed entries (used to render them elsewhere). */
	hideWarnings?: boolean
	/** When true, suppress non-warning entries (used to render warnings alone). */
	hideErrors?: boolean
	className?: string
}) {
	// Warnings the user has confirmed stay dismissed for this field even when the
	// same warning re-arrives on a re-render (the parent re-validates on every
	// keystroke/blur). Non-warning errors are never dismissed — they always
	// reflect the live `errors` prop.
	const [dismissedWarnings, setDismissedWarnings] = useState<Set<string>>(
		() => new Set(),
	)

	const errorsToRender = errors?.filter(Boolean).filter((e) => {
		const isWarning = typeof e === 'string' && e.startsWith('warning:')
		if (isWarning && hideWarnings) return false
		if (!isWarning && hideErrors) return false
		if (isWarning && dismissedWarnings.has(e as string)) return false
		return true
	})

	if (!errorsToRender?.length) return null

	const handleRemove = (errorToRemove: string) => {
		setDismissedWarnings((prev) => new Set(prev).add(errorToRemove))
	}

	return (
		<ul id={id} className={cn('flex flex-col gap-1', className)}>
			{errorsToRender.map((e, index) => {
				const isWarning = typeof e === 'string' && e.startsWith('warning:')

				const message = typeof e === 'string' ? e.replace('warning:', '') : e

				return (
					<li
						key={`${e}-${index}`}
						className={
							isWarning
								? 'flex items-center justify-between gap-2 rounded-[12px] border border-[#F6D59A] bg-[#FFFCF8] px-4 py-2 text-[14px] font-medium text-[#F59F0A]'
								: 'text-foreground-destructive flex justify-between text-[12px]'
						}
					>
						<div className="flex gap-2">
							{isWarning && (
								<TriangleAlert size={20} className="w-[20px] text-yellow-500" />
							)}
							<span className="block">{message}</span>
						</div>

						{isWarning && (
							<button
								type="button"
								onClick={() => handleRemove(e as string)}
								className="shrink-0 cursor-pointer rounded-[12px] bg-[#32302F] px-4 py-[6px] text-[13px] font-medium text-white transition-colors hover:bg-[#1a1918]"
							>
								Confirm
							</button>
						)}
					</li>
				)
			})}
		</ul>
	)
}

export function Field({
	labelProps,
	inputProps,
	errors,
	className: _className,
	errorClass,
	passwordHide = false,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: React.InputHTMLAttributes<HTMLInputElement>
	errors?: ListOfErrors
	errorClass?: string
	className?: string
	passwordHide?: boolean
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	const [showPassword, setShowPassword] = useState(false)
	return (
		<div>
			<div className="relative">
				{/* FLOATING LABEL */}
				<Label htmlFor={id} {...labelProps} />

				<Input
					id={id}
					aria-invalid={errorId ? true : undefined}
					aria-describedby={errorId}
					{...inputProps}
					type={
						passwordHide
							? showPassword
								? 'text'
								: 'password'
							: inputProps.type
					}
					className={`${inputProps.className || ''} ${
						passwordHide ? 'pr-12' : ''
					}`}
				/>

				{passwordHide && (
					<button
						type="button"
						onClick={() => setShowPassword(!showPassword)}
						className="absolute top-1/2 right-4 z-10 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-gray-600 transition-colors hover:text-gray-800 focus:outline-none"
						aria-label={showPassword ? 'Hide password' : 'Show password'}
					>
						<img
							src={showPassword ? openEye : closeEye}
							alt=""
							className="h-full w-full object-contain"
						/>
					</button>
				)}
			</div>

			{/* ERROR MESSAGE - Outside the input wrapper */}
			<div className={`min-h-[32px] px-4 pt-1 pb-3 ${errorClass}`}>
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function AnimatedField({
	labelProps,
	inputProps,
	errors,
	className: _className,
	errorClass,
	passwordHide = false,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: React.InputHTMLAttributes<HTMLInputElement>
	errors?: ListOfErrors
	errorClass?: string
	className?: string
	passwordHide?: boolean
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	const [showPassword, setShowPassword] = useState(false)

	// Ensure placeholder exists for :placeholder-shown pseudo-class to work
	// Use a space if no placeholder provided, or use the provided placeholder
	const placeholder = inputProps.placeholder || ' '

	return (
		<>
			<style>{`
				.floating-input-${id}:focus ~ label,
				.floating-input-${id}:not(:placeholder-shown) ~ label {
					top: -0.75rem;
					font-size: 0.875rem;
					font-weight: 600;
					color: ${errors?.length ? '#ef4444' : '#14b8a6'};
				}
				.floating-input-${id}:focus {
					border-color: ${errors?.length ? '#ef4444' : '#14b8a6'} !important;
				}
				.floating-input-${id}:not(:placeholder-shown) {
					border-color: ${errors?.length ? '#ef4444' : '#14b8a6'};
				}
			`}</style>
			<div>
				<div className="relative">
					<Input
						id={id}
						aria-invalid={errorId ? true : undefined}
						aria-describedby={errorId}
						{...inputProps}
						placeholder={placeholder}
						type={
							passwordHide
								? showPassword
									? 'text'
									: 'password'
								: inputProps.type
						}
						className={` floating-input-${id} ${inputProps.className || ''} ${passwordHide ? 'pr-12' : ''} [&:focus]:ring-opacity-20 transition-all duration-200 ease-out [&:focus]:ring-1 [&:focus]:ring-[#14b8a6] ${errors?.length ? '!border-[#ef4444] focus:!ring-[#ef4444]' : ''} `}
					/>

					{/* FLOATING LABEL with pure CSS animation */}
					<Label
						htmlFor={id}
						{...labelProps}
						className={`pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 bg-white px-2 text-base font-normal text-[#32302F] transition-all duration-200 ease-out`}
					/>

					{passwordHide && (
						<button
							type="button"
							onClick={() => setShowPassword(!showPassword)}
							className="absolute top-1/2 right-4 z-10 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-gray-600 transition-colors hover:text-gray-800 focus:outline-none"
							aria-label={showPassword ? 'Hide password' : 'Show password'}
						>
							<img
								src={showPassword ? openEye : closeEye}
								alt=""
								className="h-full w-full object-contain"
							/>
						</button>
					)}
				</div>

				{/* ERROR MESSAGE */}
				<div className={`min-h-[20px] px-4 pt-1 ${errorClass}`}>
					{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
				</div>
			</div>
		</>
	)
}

export function FloatingLabelField({
	labelProps,
	inputProps,
	errors,
	className,
	errorClass,
	passwordHide = false,
	rightIcon,
	rightIconClassName,
	leftIcon,
	currency = false,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: React.InputHTMLAttributes<HTMLInputElement>
	errors?: ListOfErrors
	errorClass?: string
	className?: string
	passwordHide?: boolean
	rightIcon?: React.ReactNode
	rightIconClassName?: string
	leftIcon?: React.ReactNode
	/** Sticky $ prefix (gray when empty, dark when filled) + comma formatting. */
	currency?: boolean
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	// Only REAL (non-`warning:`) errors mark the field invalid. A `warning:` entry
	// (e.g. an out-of-range amount) is advisory and shows as its own confirm box —
	// it must not trigger aria-[invalid] and leave the border stuck red.
	const errorId = hasBlockingError(errors) ? `${id}-error` : undefined
	const [showPassword, setShowPassword] = useState(false)

	const inputRef = useRef<HTMLInputElement>(null)
	const [isFocused, setIsFocused] = useState(false)

	const BASE_INPUT_CLASS =
		'text-[14px] md:text-[16px] placeholder:font-normal placeholder:text-regular rounded-[15px]'

	// Helper function to check if input has value
	const checkInputValue = () => {
		if (inputRef.current) {
			return inputRef.current.value.length > 0
		}
		// Fallback to props if ref not available
		const propValue = inputProps.value || inputProps.defaultValue || ''
		return typeof propValue === 'string' ? propValue.length > 0 : false
	}

	// Initialize state - check both props and will check DOM in effect
	const initialValue = inputProps.defaultValue || inputProps.value || ''
	const initialHasValue =
		typeof initialValue === 'string' ? initialValue.length > 0 : false
	const [hasValue, setHasValue] = useState(initialHasValue)

	const currencyRaw = currency
		? sanitizeMoneyInput(String(inputProps.value ?? ''))
		: ''
	const {
		value: _currencyValue,
		onChange: currencyOnChange,
		onKeyDown: currencyOnKeyDown,
		...restCurrencyInput
	} = inputProps
	const fieldInputProps = currency
		? {
				...restCurrencyInput,
				type: 'text' as const,
				inputMode: 'numeric' as const,
				value: formatCurrencyAmountDisplay(currencyRaw),
				onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
					const nextRaw = sanitizeMoneyInput(e.target.value)
					currencyOnChange?.({
						...e,
						target: { ...e.target, value: nextRaw },
					} as React.ChangeEvent<HTMLInputElement>)
				},
				onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
					if (e.key === '-' || e.key === '.') e.preventDefault()
					currencyOnKeyDown?.(e)
				},
			}
		: inputProps
	const fieldLeftIcon =
		leftIcon ??
		(currency ? (
			<span
				className={cn(
					'text-[16px] font-medium',
					currencyRaw.length > 0 ? 'text-[#484848]' : 'text-[#CCCCCC]',
				)}
			>
				$
			</span>
		) : undefined)

	// Callback ref to check value immediately when input is mounted
	const setInputRef = (element: HTMLInputElement | null) => {
		inputRef.current = element
		if (element) {
			// Check value immediately when element is mounted
			const currentHasValue = element.value.length > 0
			if (currentHasValue) {
				setHasValue(true)
				setIsFocused(true)
			}
			// Also check on next frame (for autofill that happens after mount)
			requestAnimationFrame(() => {
				const hasValue = element.value.length > 0
				if (hasValue) {
					setHasValue(true)
					setIsFocused(true)
				}
			})
		}
	}

	const [isAutofilled, setIsAutofilled] = useState(false)

	useEffect(() => {
		const input = document.getElementById(id) as HTMLInputElement | null
		if (!input) return

		const checkAutofill = () => {
			if (input.matches(':-webkit-autofill')) {
				setIsAutofilled(true)
			}
		}

		// Run after paint
		requestAnimationFrame(checkAutofill)
	}, [id])

	// Check value on every render by reading directly from DOM
	// This ensures we catch values that exist before effects run
	const currentHasValue = checkInputValue()

	// Check for autofill and initial value on mount and continuously
	useEffect(() => {
		const checkValue = () => {
			if (inputRef.current) {
				const currentValue = inputRef.current.value
				const hasValue = currentValue.length > 0
				setHasValue(hasValue)
				if (hasValue) {
					setIsFocused(true)
				} else if (!isFocused) {
					setIsFocused(false)
				}
			}
		}

		// Check immediately and after delays for autofill (browsers may delay autofill)
		checkValue()
		const timeout1 = setTimeout(checkValue, 10)
		const timeout2 = setTimeout(checkValue, 50)
		const timeout3 = setTimeout(checkValue, 100)
		const timeout4 = setTimeout(checkValue, 200)
		const timeout5 = setTimeout(checkValue, 500)
		const timeout6 = setTimeout(checkValue, 1000)

		// Also use MutationObserver to watch for attribute changes (autofill)
		const observer = new MutationObserver(() => {
			checkValue()
		})

		if (inputRef.current) {
			observer.observe(inputRef.current, {
				attributes: true,
				attributeFilter: ['value', 'class'],
				childList: false,
				subtree: false,
			})
		}

		return () => {
			clearTimeout(timeout1)
			clearTimeout(timeout2)
			clearTimeout(timeout3)
			clearTimeout(timeout4)
			clearTimeout(timeout5)
			clearTimeout(timeout6)
			observer.disconnect()
		}
	}, [isFocused])

	// Watch for external value changes (for controlled inputs)
	useEffect(() => {
		const currentValue = inputProps.value || inputProps.defaultValue || ''
		const propHasValue =
			typeof currentValue === 'string' ? currentValue.length > 0 : false
		const domHasValue = inputRef.current
			? inputRef.current.value.length > 0
			: false
		const hasValue = propHasValue || domHasValue

		setHasValue(hasValue)
		if (hasValue) {
			setIsFocused(true)
		} else if (inputRef.current && !inputRef.current.value) {
			setIsFocused(false)
		}
	}, [inputProps.value, inputProps.defaultValue])

	const handleFocus = () => {
		setIsFocused(true)
	}

	const handleBlur = () => {
		// Check if input has value after blur
		setTimeout(() => {
			if (inputRef.current) {
				const hasValue = inputRef.current.value.length > 0
				setHasValue(hasValue)
				if (!hasValue) {
					setIsFocused(false)
				}
			}
		}, 0)
	}

	const handleInput = () => {
		if (inputRef.current) {
			const hasValue = inputRef.current.value.length > 0
			setHasValue(hasValue)
			if (hasValue) {
				setIsFocused(true)
			}
		}
	}

	// Use layout effect to check value synchronously after DOM updates
	// This runs after every render to catch values immediately
	useLayoutEffect(() => {
		if (inputRef.current) {
			const currentValue = inputRef.current.value
			const currentHasValue = currentValue.length > 0
			// Only update if different to avoid unnecessary re-renders
			if (currentHasValue && !hasValue) {
				setHasValue(true)
				setIsFocused(true)
			} else if (!currentHasValue && hasValue && !isFocused) {
				setHasValue(false)
			}
		}
	}, [hasValue, isFocused])

	// Use the current DOM value if available, otherwise use state
	const effectiveHasValue = currentHasValue || hasValue
	// For date/time inputs, always float the label to avoid overlap with browser placeholder
	const isDateLikeInput =
		inputProps.type === 'date' ||
		inputProps.type === 'time' ||
		inputProps.type === 'datetime-local'
	const shouldFloat = isDateLikeInput || isFocused || effectiveHasValue
	// When a label is present it acts as the resting placeholder, so the input
	// placeholder only shows once the label floats. When there's no label
	// (children null/empty), show the placeholder on load instead.
	const hasLabel = labelProps.children != null && labelProps.children !== ''
	const placeholder =
		!effectiveHasValue && (shouldFloat || !hasLabel)
			? inputProps.placeholder
			: ''

	// Destructure htmlFor from labelProps to ensure we control it explicitly
	const { htmlFor: _, ...restLabelProps } = labelProps

	// React Spring animations for label and input
	const labelSpring = useSpring({
		top: shouldFloat ? -12 : 0,
		fontSize: shouldFloat ? 14 : 18,
		config: gentleSpring,
	})

	const inputScaleSpring = useSpring({
		scale: isFocused && !currency ? 1.01 : 1,
		config: gentleSpring,
	})

	const errorSpring = useSpring({
		opacity: errorId ? 1 : 0,
		y: errorId ? 0 : -10,
		config: smoothSpring,
	})

	const hasWarning = errors?.some(
		(e) => typeof e === 'string' && e.startsWith('warning:'),
	)

	return (
		<div className={cn('relative', className)}>
			{/* Warnings float ABOVE the field as an overlay (with a Confirm action)
			    so the field stays in place instead of being pushed down; normal
			    errors stay below the field. */}
			{hasWarning ? (
				<ErrorList
					errors={errors}
					hideErrors
					className="absolute right-0 bottom-full left-0 z-20 mb-4"
				/>
			) : null}
			<div className="relative">
				<style>{`
					/* Fix autofill background - prevents native blue background */
					input.floating-label-input-${id}:-webkit-autofill,
					input.floating-label-input-${id}:-webkit-autofill:hover,
					input.floating-label-input-${id}:-webkit-autofill:focus,
					input.floating-label-input-${id}:-webkit-autofill:active {
						-webkit-box-shadow: 0 0 0 1000px white inset !important;
						-webkit-text-fill-color: #484848 !important;
						transition: background-color 5000s ease-in-out 0s;
					}

					/* Add focus-visible ring */
					input.floating-label-input-${id}:focus-visible {
						box-shadow: none !important;
					}

					/* Reliable autofill detection: Chrome starts this (no-op) animation
					   the instant it autofills the field — even after a redirect, where
					   the value/attribute polling above races and misses it. The
					   onAnimationStart handler below then floats the label so it never
					   overlaps the autofilled text. */
					@keyframes floating-autofill-${id} { from {} to {} }
					input.floating-label-input-${id}:-webkit-autofill {
						animation-name: floating-autofill-${id};
						animation-duration: 0.01s;
					}
				`}</style>
				<animated.div style={inputScaleSpring}>
					<Input
						ref={setInputRef}
						id={id}
						aria-invalid={errorId ? true : undefined}
						aria-describedby={errorId}
						{...fieldInputProps}
						placeholder={placeholder}
						type={
							passwordHide
								? showPassword
									? 'text'
									: 'password'
								: fieldInputProps.type
						}
						// className={`floating-label-input-${id} ${inputProps.className || ''} ${
						// 	passwordHide ? 'pr-12' : ''
						// } transition-all duration-300 ease-out focus:outline-none focus:ring-0`}
						className={cn(
							`floating-label-input-${id}`,
							BASE_INPUT_CLASS,
							fieldInputProps.className,
							fieldLeftIcon && (currency ? 'pl-8!' : 'pl-12!'),
							(passwordHide || rightIcon) && 'pr-12',
							'transition-all duration-300 ease-out focus:ring-0 focus:outline-none',
							// Uniform value text: 14px on mobile, 16px on ≥md, #484848 —
							// wins over the per-field sizes each usage passes.
							'text-[14px]! text-[#484848]! md:text-[16px]!',
						)}
						onFocus={(e) => {
							handleFocus()
							fieldInputProps.onFocus?.(e)
						}}
						onBlur={(e) => {
							handleBlur()
							fieldInputProps.onBlur?.(e)
						}}
						onInput={(e) => {
							handleInput()
							if (fieldInputProps.onInput) {
								fieldInputProps.onInput(e)
							}
						}}
						onAnimationStart={(e) => {
							if (e.animationName === `floating-autofill-${id}`) {
								setIsAutofilled(true)
								setHasValue(true)
								setIsFocused(true)
							}
							fieldInputProps.onAnimationStart?.(e)
						}}
					/>
				</animated.div>

				{/* Floating Label with React Spring animation */}
				{/* <animated.div
					style={{
						position: 'absolute',
						left: '25px',
						top: shouldFloat ? labelSpring.top : '50%',
						transform: shouldFloat ? 'translateY(0)' : 'translateY(-50%)',
						pointerEvents: 'none',
					}}
				>
					<label
						htmlFor={id}
						{...restLabelProps}
						style={{
							fontSize: `${labelSpring.fontSize}px`,
							...restLabelProps.style,
						}}
						className={cn(
							'text-[#32302F]',
							shouldFloat
								? 'bg-white px-[3px] text-[16px]'
								: 'bg-transparent font-normal',
							restLabelProps.className,
						)}
					>
						{restLabelProps.children}
					</label>
				</animated.div> */}

				{/* Floating Label — skipped when there's no label text, so an empty
				    label's background strip doesn't punch a gap in the field's top
				    border (e.g. the pet-weight select passes children: null). */}
				{hasLabel && (
					<animated.div
						style={{
							position: 'absolute',
							left: fieldLeftIcon && !(shouldFloat || isAutofilled) ? 32 : 25,
							right: 16,
							top: shouldFloat || isAutofilled ? 0 : '50%',
							transform:
								shouldFloat || isAutofilled
									? 'translateY(-12px)'
									: 'translateY(-50%)',
							pointerEvents: 'none',
							overflow: 'hidden',
						}}
					>
						<label
							htmlFor={id}
							{...restLabelProps}
							className={cn(
								'inline-block max-w-full truncate text-[#32302F]',
								shouldFloat || isAutofilled
									? 'bg-[#FBFDFD] px-[3px]'
									: 'bg-transparent font-normal',
								restLabelProps.className,
							)}
						>
							{restLabelProps.children}
						</label>
					</animated.div>
				)}

				{passwordHide && (
					<button
						type="button"
						onClick={() => setShowPassword(!showPassword)}
						className="absolute top-1/2 right-4 z-10 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-gray-600 transition-colors hover:text-gray-800 focus:outline-none"
						aria-label={showPassword ? 'Hide password' : 'Show password'}
					>
						<img
							src={showPassword ? openEye : closeEye}
							alt=""
							className="h-full w-full object-contain"
						/>
					</button>
				)}
				{fieldLeftIcon ? (
					<div className="pointer-events-none absolute top-1/2 left-4 z-10 flex -translate-y-1/2 items-center justify-center">
						{fieldLeftIcon}
					</div>
				) : null}

				{rightIcon && (
					<div
						className={cn(
							'pointer-events-none absolute top-1/2 z-10 flex -translate-y-1/2 items-center justify-center',
							rightIconClassName ?? 'right-4',
						)}
					>
						{rightIcon}
					</div>
				)}
			</div>

			{/* ERROR MESSAGE with React Spring animation */}
			<animated.div
				style={errorSpring}
				className={`mx-5 mt-1 min-h-auto ${errorClass || ''}`}
			>
				{errorId ? (
					<ErrorList id={errorId} errors={errors} hideWarnings />
				) : null}
			</animated.div>
		</div>
	)
}

interface FloatingGooglePlacesFieldProps {
	label: string
	required?: boolean
	// biome-ignore lint/suspicious/noExplicitAny: react-google-places-autocomplete uses react-select types which require any
	value: any
	// biome-ignore lint/suspicious/noExplicitAny: react-google-places-autocomplete uses react-select types which require any
	onChange: (place: any) => void
	error?: string
	apiKey: string
	/** Places autocomplete bias — e.g. the property form restricts suggestions
	 * to Canadian street addresses. Omitted = Google's default mix. */
	autocompletionRequest?: {
		types?: string[]
		componentRestrictions?: { country: string[] }
	}
	/** Resting border colour — defaults to the screening #CCCCCC; the property
	 * v2 form passes #F0F0F0 to match its lighter field border. */
	borderColor?: string
	/** Denser variant (property v2 form): smaller control height, 12px radius and
	 * a 14px label/text, to match that form's compact field styling. Default
	 * keeps the original screening size. */
	compact?: boolean
	/** Keep the label floated and the search icon/placeholder visible even when
	 * the field is empty and unfocused (search-style resting look). Default keeps
	 * the label centered until focus/value. */
	alwaysFloat?: boolean
}

export function FloatingGooglePlacesField({
	label,
	value,
	onChange,
	error,
	apiKey,
	autocompletionRequest,
	borderColor = '#CCCCCC',
	compact = false,
	alwaysFloat = false,
}: FloatingGooglePlacesFieldProps) {
	const [isFocused, setIsFocused] = useState(false)
	const [isMounted, setIsMounted] = useState(false)

	useEffect(() => {
		setIsMounted(true)
	}, [])

	const hasValue = !!value
	const shouldFloat = alwaysFloat || isFocused || hasValue

	const labelId = useId()
	const inputId = `${labelId}-input`

	// Compact sizing shared by the SSR fallback and the live control so both
	// render identically. Defaults preserve the original 50px / 15px screening look.
	const controlHeight = compact ? 40 : 50
	const controlRadius = compact ? 12 : 15
	const iconTopClass = compact ? 'top-[20px]' : 'top-[25px]'
	const iconLeftClass = compact ? 'left-[18px]' : 'left-[25px]'
	const controlPaddingLeft = compact ? 34 : 40
	const labelClass = cn(
		'pointer-events-none absolute z-20 transition-all duration-200',
		// Compact aligns its floated label with the sibling FloatField label
		// (left-3.5). The floated `-top-2.5` cancels the wrapping `pt-2` (8px) so
		// the label lands at the same height as FloatField's `-top-0.5`.
		compact ? 'left-3.5' : 'left-[25px]',
		shouldFloat
			? compact
				? '-top-1.5 bg-[#FBFDFD] px-1 text-sm leading-none font-normal text-[#32302F]'
				: '-top-[10px] bg-[#FBFDFD] px-1 text-[14px] leading-none text-[#32302F] md:text-[16px]'
			: compact
				? 'text-primary top-[20px] -translate-y-1/2 text-sm'
				: 'text-primary top-[25px] -translate-y-1/2 text-[14px] md:text-[16px]',
	)

	// Don't render GooglePlacesAutocomplete during SSR
	if (!isMounted || typeof window === 'undefined') {
		return (
			<div className="relative">
				<label htmlFor={inputId} id={labelId} className={labelClass}>
					{label}
				</label>
				{shouldFloat && (
					<div
						className={cn(
							'pointer-events-none absolute z-10 -translate-y-1/2 text-gray-400',
							iconLeftClass,
							iconTopClass,
						)}
					>
						<Search className="h-4 w-4" />
					</div>
				)}
				<div
					className="bg-[#FBFDFD]"
					style={{
						height: `${controlHeight}px`,
						borderRadius: `${controlRadius}px`,
						border: `1px solid ${borderColor}`,
					}}
				/>
			</div>
		)
	}

	return (
		<div className="relative">
			{/* Address value/placeholder/input text: 14px on mobile, 16px on ≥md —
			    matches the other screening fields. Inline react-select styles can't
			    carry media queries, so it's done here via the classNamePrefix. */}
			<style>{`
				.ttrs__single-value, .ttrs__placeholder, .ttrs__input input { font-size: 14px; }
				@media (min-width: 768px) {
					.ttrs__single-value, .ttrs__placeholder, .ttrs__input input { font-size: ${compact ? 14 : 16}px; }
				}
			`}</style>
			{/* Floating Label */}
			<label htmlFor={inputId} id={labelId} className={labelClass}>
				{label}
			</label>
			{/* Search Icon – only when placeholder visible. Pinned to the control's
			    centre (iconTopClass), not the container's — otherwise the
			    validation message, which renders inside this relative wrapper,
			    grows the height and `top-1/2` would drag the icon below centre. */}
			{shouldFloat && (
				<div
					className={cn(
						'pointer-events-none absolute z-10 -translate-y-1/2 text-[#32302F]',
						iconLeftClass,
						iconTopClass,
					)}
				>
					<Search className="h-4 w-4" />
				</div>
			)}

			<GooglePlacesAutocomplete
				apiKey={apiKey}
				// Optional caller bias (e.g. the property form restricts to Canadian
				// street addresses); screening callers stay unconstrained.
				autocompletionRequest={autocompletionRequest}
				selectProps={{
					value,
					onChange,
					onFocus: () => setIsFocused(true),
					onBlur: () => setIsFocused(false),
					menuPortalTarget:
						typeof document !== 'undefined' ? document.body : undefined,
					placeholder: shouldFloat ? 'Search Address' : '',
					inputId: inputId,
					'aria-labelledby': labelId,
					// Stable class prefix so the <style> block below can size the value /
					// placeholder / input text responsively (react-select inline styles
					// can't hold media queries).
					classNamePrefix: 'ttrs',
					// react-select paints control/input/value layers from the base
					// `neutral0` token (white by default) — override it so the whole
					// field uses #FBFDFD, not just the control box.
					theme: (theme) => ({
						...theme,
						colors: { ...theme.colors, neutral0: '#FBFDFD' },
					}),
					styles: {
						control: (provided, state) => ({
							...provided,
							height: `${controlHeight}px`,
							borderRadius: `${controlRadius}px`,
							backgroundColor: '#FBFDFD',
							paddingLeft: `${controlPaddingLeft}px`,
							cursor: 'pointer',
							// Single 1px border in every state (error stays red) so the field
							// matches the other screening inputs — no 2px double-width border.
							border: error
								? '1px solid #B33044'
								: state.isFocused
									? '1px solid #32302F'
									: `1px solid ${borderColor}`,
							boxShadow: 'none',
							// Keep the resting border on hover (no thick black hover outline).
							'&:hover': {
								border: error
									? '1px solid #B33044'
									: `1px solid ${borderColor}`,
							},
						}),

						// Strip the inner search input's own outline/border so only the
						// control box draws the field border. Value text colour is #484848;
						// the responsive size (14px → 16px) is set via the <style> block
						// below since react-select inline styles can't do media queries.
						input: (base) => ({
							...base,
							outline: 'none',
							border: 0,
							boxShadow: 'none',
							color: '#484848',
						}),

						// The selected address text — same #484848 as every other field.
						singleValue: (base) => ({
							...base,
							color: '#484848',
						}),

						// It's an address search, not a dropdown — drop the chevron and the
						// vertical separator bar so only the leading search icon remains.
						dropdownIndicator: () => ({ display: 'none' }),
						indicatorSeparator: () => ({ display: 'none' }),

						menuPortal: (base) => ({
							...base,
							zIndex: 9999,
							// Radix Dialog sets pointer-events:none on the rest of the
							// page. Suggestions are portaled to document.body, so they
							// must opt back in or a click never selects the place.
							pointerEvents: 'auto',
						}),
					},
				}}
			/>

			{/* Error */}
			{error && (
				<div className="mt-2">
					<p className="text-[12px] font-[400] text-[#be123d]">{error}</p>
				</div>
			)}
		</div>
	)
}

export function OTPField({
	labelProps,
	inputProps,
	errors,
	className,
	errorClass,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: Partial<OTPInputProps & { render: never }>
	errors?: ListOfErrors
	errorClass?: string
	className?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined

	// const slotClass =
	// 	'border h-[60px] flex-1 max-w-[120px] min-w-[80px] bg-white rounded-[30px] border-[#CCCCCC] text-center'
	const slotClass =
		'border flex-1 text-center bg-white rounded-[30px] border-[#CCCCCC] ' +
		'h-[44px] min-w-[44px] text-base ' + // mobile (smaller)
		'sm:h-[60px] sm:min-w-[80px] sm:max-w-[120px] sm:text-xl' //  desktop same as before

	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />
			<InputOTP
				pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
				maxLength={6}
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				pasteTransformer={(pasted) => pasted.replace(/[^a-zA-Z0-9]/g, '')}
				{...inputProps}
			>
				<div className="flex w-full justify-between gap-2 sm:gap-3">
					{[0, 1, 2, 3, 4, 5].map((index) => (
						<InputOTPSlot key={index} index={index} className={slotClass} />
					))}
				</div>
			</InputOTP>
			<div className={`min-h-[32px] px-4 pt-1 pb-3 ${errorClass}`}>
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function TextareaField({
	labelProps,
	textareaProps,
	errors,
	errorClass,
	className,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	textareaProps: React.TextareaHTMLAttributes<HTMLTextAreaElement>
	errors?: ListOfErrors
	errorClass?: string
	className?: string
}) {
	const fallbackId = useId()
	const id = textareaProps.id ?? textareaProps.name ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />
			<Textarea
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...textareaProps}
			/>
			<div className={`min-h-[32px] px-4 pt-1 pb-3 ${errorClass}`}>
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function CheckboxField({
	labelProps,
	buttonProps,
	errors,
	className,
	errorClass,
}: {
	labelProps: React.ComponentProps<'label'>
	buttonProps: CheckboxProps & {
		name: string
		form: string
		value?: string
	}
	errors?: ListOfErrors
	errorClass?: string
	className?: string
}) {
	const { key, defaultChecked, ...checkboxProps } = buttonProps
	const fallbackId = useId()
	const checkedValue = buttonProps.value ?? 'on'
	const input = useInputControl({
		key,
		name: buttonProps.name,
		formId: buttonProps.form,
		initialValue: defaultChecked ? checkedValue : undefined,
	})
	const id = buttonProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined

	return (
		<div className={className}>
			<div className="flex gap-2">
				<Checkbox
					{...checkboxProps}
					id={id}
					aria-invalid={errorId ? true : undefined}
					aria-describedby={errorId}
					checked={input.value === checkedValue}
					onCheckedChange={(state) => {
						input.change(state.valueOf() ? checkedValue : '')
						buttonProps.onCheckedChange?.(state)
					}}
					onFocus={(event) => {
						input.focus()
						buttonProps.onFocus?.(event)
					}}
					onBlur={(event) => {
						input.blur()
						buttonProps.onBlur?.(event)
					}}
					type="button"
				/>
				<Label
					htmlFor={id}
					{...labelProps}
					className="text-body-xs text-muted-foreground self-center"
				/>
			</div>
			<div className={`px-4 pt-1 pb-3 ${errorClass}`}>
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function PhoneInputField({
	labelProps,
	inputProps,
	errors,
	className,
	errorClass,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: React.ComponentProps<typeof PhoneInput>
	errors?: ListOfErrors
	className?: string
	errorClass?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	return (
		<div className={className}>
			<Label htmlFor={id} {...labelProps} />
			<PhoneInput
				id={id}
				aria-invalid={errorId ? true : undefined}
				aria-describedby={errorId}
				{...inputProps}
				className={cn('w-full', inputProps.className)}
			/>
			<div className={`min-h-[32px] px-4 pt-1 pb-3 ${errorClass}`}>
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

export function FloatingPhoneInputField({
	labelProps,
	inputProps,
	errors,
	className,
	errorClass,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	inputProps: React.ComponentProps<typeof PhoneInput>
	errors?: ListOfErrors
	className?: string
	errorClass?: string
}) {
	const fallbackId = useId()
	const id = inputProps.id ?? fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined
	const hasError = Boolean(errors?.filter(Boolean).length)

	const [isFocusWithin, setIsFocusWithin] = useState(false)

	const [hasValue, setHasValue] = useState(
		Boolean(inputProps.value || inputProps.defaultValue),
	)

	// Track the full E.164 value so the form submits "+<country><digits>"
	const [e164Value, setE164Value] = useState(
		(inputProps.value as string) || (inputProps.defaultValue as string) || '',
	)

	const shouldFloat = isFocusWithin || hasValue

	// Strip `name` from the visible input — the hidden input carries it instead
	const { name: fieldName, ...phoneInputProps } = inputProps

	return (
		<div className={className}>
			<input type="hidden" name={fieldName} value={e164Value} />
			<div
				className="relative"
				onFocusCapture={() => setIsFocusWithin(true)}
				onBlurCapture={(e) => {
					const next = e.relatedTarget as HTMLElement | null
					if (next && e.currentTarget.contains(next)) return
					setIsFocusWithin(false)
				}}
			>
				<PhoneInput
					id={id}
					aria-invalid={errorId ? true : undefined}
					aria-describedby={errorId}
					{...phoneInputProps}
					placeholder={shouldFloat ? 'Enter Phone Number' : ''}
					className={cn(
						'w-full cursor-text rounded-[15px] border border-[#CCCCCC] bg-[#FBFDFD] transition-all',
						// Red outline when the field has a validation error.
						hasError && 'border-[#B33044]!',

						// input styles
						'md:[&_input]:px-[2px]',
						'[&_input]:px-0',
						'[&_input]:py-[24px]',
						'[&_input]:text-[14px]',
						'md:[&_input]:text-[16px]',
						'[&_input]:text-[#484848]',
						'[&_input]:leading-normal',
						'[&_input]:bg-transparent',
						'[&_input]:outline-none',
						'[&_input]:border-0',
						'[&_input::placeholder]:text-[#CCCCCC]',
						'[&_input::placeholder]:text-[12px]',
						'md:[&_input::placeholder]:text-[16px]',

						// country button styles (ALWAYS clickable)
						'[&_button]:border-0',
						'[&_button]:border-r-0',
						'[&_button]:px-4',
						'[&_button]:py-[22px]',
						'[&_button]:rounded-l-[15px]',
						'[&_button]:flex',
						'[&_button]:items-center',
						'[&_button]:transition-opacity [&_button]:duration-200',

						// hide visually until float (no pointer-events!)
						shouldFloat ? '[&_button]:opacity-100' : '[&_button]:opacity-0',

						phoneInputProps.className,
					)}
					onChange={(value) => {
						const val = value || ''
						setHasValue(Boolean(val))
						setE164Value(val as string)
						inputProps.onChange?.(value)
					}}
				/>

				{/* FLOATING LABEL */}
				<label
					htmlFor={id}
					className={cn(
						'pointer-events-none absolute left-[25px] transition-all duration-200',
						shouldFloat
							? 'top-[-12px] bg-[#FBFDFD] px-[3px] text-[16px] text-[#32302F]'
							: 'top-1/2 -translate-y-1/2 text-[16px] text-[#32302F]',
						labelProps.className,
					)}
				>
					{labelProps.children}
				</label>
			</div>

			{/* ERROR */}
			<div className={cn('mx-5 mt-1', errorClass)}>
				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
			</div>
		</div>
	)
}

// export function FloatingSelectField({
// 	labelProps,
// 	selectProps,
// 	triggerProps,
// 	errors,
// 	className,
// 	errorClass,
// 	children,
// }: {
// 	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
// 	selectProps: {
// 		value?: string
// 		onValueChange: (value: string) => void
// 		defaultValue?: string
// 		disabled?: boolean
// 	}
// 	triggerProps?: React.ComponentPropsWithoutRef<typeof SelectTrigger>
// 	errors?: ListOfErrors
// 	className?: string
// 	errorClass?: string
// 	children: React.ReactNode
// }) {
// 	const fallbackId = useId()
// 	const id = triggerProps?.id ?? fallbackId
// 	const errorId = errors?.length ? `${id}-error` : undefined

// 	const [isOpen, setIsOpen] = useState(false)
// 	const [isFocusWithin, setIsFocusWithin] = useState(false)
// 	const hasValue = Boolean(selectProps.value || selectProps.defaultValue)
// 	const shouldFloat = isFocusWithin || hasValue || isOpen

// 	return (
// 		<div className={className}>
// 			<div
// 				className="relative"
// 				onFocusCapture={() => setIsFocusWithin(true)}
// 				onBlurCapture={(e) => {
// 					const next = e.relatedTarget as HTMLElement | null
// 					if (next && e.currentTarget.contains(next)) return
// 					setIsFocusWithin(false)
// 				}}
// 			>
// 				<Select
// 					{...selectProps}
// 					onOpenChange={(open) => {
// 						setIsOpen(open)
// 						if (open) {
// 							setIsFocusWithin(true)
// 						}
// 					}}
// 				>
// 					<SelectTrigger
// 						{...triggerProps}
// 						id={id}
// 						aria-invalid={errorId ? true : undefined}
// 						aria-describedby={errorId}
// 						className={cn(
// 							'border border-[#CCCCCC] rounded-[30px] px-[33px] py-[24px] h-auto text-[16px] text-[#484848] placeholder:text-[#CCCCCC] placeholder:text-regular transition-all',
// 							shouldFloat && 'border-[#000000]',
// 							triggerProps?.className,
// 						)}
// 					>
// 						<SelectValue placeholder={shouldFloat ? '' : 'Select an option'} />
// 					</SelectTrigger>
// 					<SelectContent>{children}</SelectContent>
// 				</Select>

// 				{/* FLOATING LABEL */}
// 				<label
// 					htmlFor={id}
// 					className={cn(
// 						'absolute left-[25px] transition-all duration-200 pointer-events-none',
// 						shouldFloat
// 							? 'top-[-12px] text-[16px] bg-white px-[3px] text-[#32302F]'
// 							: 'top-1/2 -translate-y-1/2 text-[16px] text-[#32302F]',
// 						labelProps.className,
// 					)}
// 				>
// 					{labelProps.children}
// 				</label>
// 			</div>

// 			{/* ERROR */}
// 			<div className={cn('mx-5 mt-1', errorClass)}>
// 				{errorId ? <ErrorList id={errorId} errors={errors} /> : null}
// 			</div>
// 		</div>
// 	)
// }

type Option = {
	label: string
	value: string
	icon?: React.ReactNode
	image?: string
	allowInput?: boolean
	// 🔽 NEW FLAGS
	showDividerBefore?: boolean
	showDividerAfter?: boolean
	headingBefore?: string
	headingAfter?: string
	inputPlaceholder?: string
	dividerAfter?: boolean
}

type DropdownHeaderConfig = {
	show?: boolean
	text?: string
	subText?: string
}

type SingleSelectProps = {
	value?: string
	onChange: (value: string) => void
	multiple?: false
}

type MultiSelectProps = {
	value?: string[] | string
	onChange: (value: string[]) => void
	multiple: true
}

type CommonProps = {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	options: Option[]
	errors?: string[]
	className?: string
	errorClass?: string
	dropdownHeader?: DropdownHeaderConfig
	placeholder?: string
	/** Tighter height/padding for narrow layouts (e.g. grid cells). */
	compact?: boolean
	/** Overrides the default trigger field classes (radius, bg, border, …). */
	inputClassName?: string
	/** Render the option list in a portal so it is not clipped by overflow parents. */
	portal?: boolean
}

export function FloatingSelectField(
	props: CommonProps & (SingleSelectProps | MultiSelectProps),
) {
	const {
		labelProps,
		options,
		errors,
		className,
		errorClass,
		dropdownHeader,
		placeholder = 'Select option',
		compact = false,
		inputClassName,
		portal = false,
	} = props

	const multiple = props.multiple ?? false
	const value = props.value

	const id = useId()
	const ref = useRef<HTMLDivElement>(null)
	const menuRef = useRef<HTMLDivElement>(null)
	const [open, setOpen] = useState(false)
	const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({})

	const [customInputs, setCustomInputs] = useState<Record<string, string>>({})

	// normalize value
	const selectedValues: string[] = Array.isArray(value)
		? value
		: typeof value === 'string' && value.length
			? value.split(',').filter(Boolean)
			: []

	// ---------------- LABEL ----------------
	const selectedLabel = options
		.map((o) => {
			const addUtilityValue = selectedValues.find((v) =>
				v.startsWith('add-utility:'),
			)

			if (o.value === 'add-utility' && addUtilityValue) {
				const [, type, amount] = addUtilityValue.split(':')
				return `${o.label} (${type.charAt(0).toUpperCase() + type.slice(1)}: ${amount})`
			}

			if (selectedValues.includes(o.value)) {
				const custom = customInputs[o.value]
				return custom ? `${o.label} (${custom})` : o.label
			}

			return null
		})
		.filter(Boolean)
		.join(', ')

	// outside click (also ignore the portaled menu)
	useEffect(() => {
		const handler = (e: MouseEvent) => {
			const target = e.target as Node
			if (ref.current?.contains(target)) return
			if (menuRef.current?.contains(target)) return
			setOpen(false)
		}
		document.addEventListener('mousedown', handler)
		return () => document.removeEventListener('mousedown', handler)
	}, [])

	useLayoutEffect(() => {
		if (!open || !portal) return
		const update = () => {
			if (!ref.current) return
			const rect = ref.current.getBoundingClientRect()
			const menuHeight = 280
			const spaceBelow = window.innerHeight - rect.bottom
			const openUp = spaceBelow < menuHeight && rect.top > spaceBelow
			setMenuStyle({
				position: 'fixed',
				left: rect.left,
				width: rect.width,
				zIndex: 200,
				pointerEvents: 'auto',
				...(openUp
					? { bottom: window.innerHeight - rect.top + 8 }
					: { top: rect.bottom + 8 }),
			})
		}
		update()
		window.addEventListener('scroll', update, true)
		window.addEventListener('resize', update)
		return () => {
			window.removeEventListener('scroll', update, true)
			window.removeEventListener('resize', update)
		}
	}, [open, portal])

	// ---------------- SELECT ----------------
	const handleSelect = (val: string) => {
		// ONLY add-utility special
		if (val === 'add-utility') {
			if (!selectedValues.includes('add-utility')) {
				;(props as MultiSelectProps).onChange([
					...selectedValues,
					'add-utility',
				])
			}
			return
		}

		if (multiple) {
			const updated = selectedValues.includes(val)
				? selectedValues.filter((v) => v !== val)
				: [...selectedValues, val]
			;(props as MultiSelectProps).onChange(updated)
		} else {
			;(props as SingleSelectProps).onChange(val)
			setOpen(false)
		}
	}

	// ---------------- ADD UTILITY ----------------
	const handleAddUtilityChange = (key: 'type' | 'text', value: string) => {
		setCustomInputs((prev) => {
			const updated = {
				...prev,
				[`add-utility-${key}`]: value,
			}

			const type = updated['add-utility-type']
			const text = updated['add-utility-text']

			if (type && text) {
				const finalValue = `add-utility:${type}:${text}`

				if (multiple) {
					const cleaned = selectedValues.filter(
						(v) => !v.startsWith('add-utility:'),
					)
					;(props as MultiSelectProps).onChange([
						...cleaned,
						'add-utility',
						finalValue,
					])
				} else {
					;(props as SingleSelectProps).onChange(finalValue)
				}
			}

			return updated
		})
	}

	// ---------------- NORMAL INPUT ----------------
	const handleCustomInputChange = (optionValue: string, input: string) => {
		setCustomInputs((prev) => ({ ...prev, [optionValue]: input }))

		if (!multiple) {
			;(props as SingleSelectProps).onChange(optionValue)
		}
	}

	const menu =
		open && (!portal || Boolean(menuStyle.position)) ? (
			<div
				ref={menuRef}
				className={cn(
					'floating-select-menu rounded-[20px] bg-[#FBFDFD] shadow-[0px_0px_16px_0px_#0000000A,0px_0px_4px_0px_#0000000A]',
					portal
						? 'pointer-events-auto'
						: 'absolute left-0 z-50 mt-2 w-max min-w-full',
				)}
				style={portal ? menuStyle : undefined}
				onPointerDown={(e) => e.stopPropagation()}
			>
				{dropdownHeader?.show && (
					<div className="border-b px-6 py-3">
						<div className="text-[14px] font-medium text-[#32302F]">
							{dropdownHeader.text}
						</div>

						{dropdownHeader.subText && (
							<div className="text-[12px] text-[#7A7A7A]">
								{dropdownHeader.subText}
							</div>
						)}
					</div>
				)}

				<div
					data-scroll-lock-scrollable
					className="max-h-[280px] min-h-0 overflow-x-hidden overflow-y-auto overscroll-contain"
					onWheel={(e) => e.stopPropagation()}
					onTouchMove={(e) => e.stopPropagation()}
				>
					{options.map((option) => {
						const isSelected = selectedValues.includes(option.value)

						const showCustomInput = isSelected && option.allowInput

						const showUtilityDropdown =
							option.value === 'add-utility' &&
							selectedValues.includes('add-utility')

						return (
							<div key={option.value}>
								<button
									type="button"
									onClick={() => handleSelect(option.value)}
									className={`flex w-full items-center justify-between hover:bg-[#F6F6F6] ${
										compact ? 'px-4 py-3' : 'px-6 py-4'
									}`}
								>
									<div className="flex min-w-0 flex-1 items-center gap-3">
										{option.icon ? (
											<span className="flex h-5 w-5 shrink-0 items-center justify-center">
												{option.icon}
											</span>
										) : option.image ? (
											<img
												src={option.image}
												alt={option.label}
												className="h-5 w-5 shrink-0 object-contain"
											/>
										) : null}

										<span className="truncate text-left">{option.label}</span>
									</div>

									<span
										className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
											isSelected ? 'border-[#32302F]' : 'border-[#CCCCCC]'
										}`}
									>
										{isSelected && (
											<span className="h-2.5 w-2.5 rounded-full bg-[#32302F]" />
										)}
									</span>
								</button>

								{/* NORMAL INPUT (Parking etc.) */}
								{showCustomInput && (
									<div className="px-6 pb-4">
										<input
											type="text"
											placeholder="Add Cost"
											className="w-full rounded-[10px] border px-3 py-2"
											value={customInputs[option.value] ?? ''}
											onChange={(e) =>
												handleCustomInputChange(option.value, e.target.value)
											}
										/>
									</div>
								)}

								{/* ADD UTILITY */}
								{showUtilityDropdown && (
									<div className="flex gap-2 px-6 pb-4">
										<select
											className="flex-1 rounded-[10px] border px-3 py-2"
											value={customInputs['add-utility-type'] ?? ''}
											onChange={(e) =>
												handleAddUtilityChange('type', e.target.value)
											}
										>
											<option value="">Select</option>
											<option value="tenant">Tenant</option>
											<option value="owner">Owner</option>
										</select>

										<input
											type="text"
											placeholder="Add Utilities"
											className="flex-1 rounded-[10px] border px-3 py-2"
											value={customInputs['add-utility-text'] ?? ''}
											onChange={(e) =>
												handleAddUtilityChange('text', e.target.value)
											}
										/>
									</div>
								)}
							</div>
						)
					})}
				</div>
			</div>
		) : null

	return (
		<div ref={ref} className={`${className ?? ''} relative`}>
			<FloatingLabelField
				inputProps={{
					id,
					type: 'text',
					readOnly: true,
					value: selectedLabel,
					placeholder,
					onClick: () => setOpen(!open),
					className: cn(
						'placeholder:text-[#CCCCCC] placeholder:text-regular text-[14px] text-regular text-[#484848] md:text-[16px] border rounded-[15px] bg-[#FBFDFD] cursor-pointer',
						compact ? 'px-4 py-2' : 'px-[33px] py-[24px]',
						errors?.filter(Boolean).length ? 'border-[#B33044]!' : '',
						inputClassName,
					),
				}}
				labelProps={labelProps}
				errors={errors}
				errorClass={errorClass}
				rightIcon={
					<ChevronDown
						className={cn(
							'transition-transform',
							compact ? 'h-3.5 w-3.5' : 'h-6 w-6',
							open && 'rotate-180',
						)}
					/>
				}
				rightIconClassName={compact ? 'right-2' : undefined}
			/>

			{portal && typeof document !== 'undefined' && menu
				? createPortal(menu, document.body)
				: menu}
		</div>
	)
}

export function FloatingSelectFieldLocked(
	props: CommonProps & (SingleSelectProps | MultiSelectProps),
) {
	const {
		labelProps,
		options,
		errors,
		className,
		dropdownHeader,
		placeholder = 'Select option',
	} = props

	const multiple = props.multiple ?? false
	const value = props.value

	const id = useId()
	const ref = useRef<HTMLDivElement>(null)
	const [open, setOpen] = useState(false)
	const [customInputs, setCustomInputs] = useState<Record<string, string>>({})

	// normalize value
	const selectedValues: string[] = Array.isArray(value)
		? value
		: typeof value === 'string' && value.length
			? value.split(',').filter(Boolean)
			: []

	// ---------- helper ----------
	const hasUnfilledRequiredInput = () => {
		for (const option of options) {
			if (
				option.allowInput &&
				selectedValues.includes(option.value) &&
				!customInputs[option.value]?.trim()
			) {
				return true
			}
		}

		if (selectedValues.includes('add-utility')) {
			const type = customInputs['add-utility-type']
			const text = customInputs['add-utility-text']
			if (!type || !text?.trim()) return true
		}

		return false
	}

	// ---------- LABEL ----------
	const selectedLabel = options
		.map((o) => {
			const addUtilityValue = selectedValues.find((v) =>
				v.startsWith('add-utility:'),
			)

			if (o.value === 'add-utility' && addUtilityValue) {
				const [, type, amount] = addUtilityValue.split(':')
				return `${o.label} (${type}: ${amount})`
			}

			if (selectedValues.includes(o.value)) {
				const custom = customInputs[o.value]
				return custom ? `${o.label} (${custom})` : o.label
			}

			return null
		})
		.filter(Boolean)
		.join(', ')

	// ---------- outside click ----------
	useEffect(() => {
		const handler = (e: MouseEvent) => {
			if (ref.current && !ref.current.contains(e.target as Node)) {
				if (hasUnfilledRequiredInput()) return
				setOpen(false)
			}
		}
		document.addEventListener('mousedown', handler)
		return () => document.removeEventListener('mousedown', handler)
	}, [selectedValues, customInputs])

	// ---------- select ----------
	const handleSelect = (option: Option) => {
		const isSelected = selectedValues.includes(option.value)

		// toggle selection
		let updated: string[] = []

		if (multiple) {
			updated = isSelected
				? selectedValues.filter((v) => v !== option.value)
				: [...selectedValues, option.value]
			;(props as MultiSelectProps).onChange(updated)
		} else {
			;(props as SingleSelectProps).onChange(option.value)
		}

		if (!option.allowInput) {
			setOpen(false)
		}
	}

	// ---------- inputs ----------
	const handleCustomInputChange = (key: string, value: string) => {
		setCustomInputs((prev) => ({ ...prev, [key]: value }))
	}

	const handleAddUtilityChange = (key: 'type' | 'text', value: string) => {
		setCustomInputs((prev) => {
			const updated = {
				...prev,
				[`add-utility-${key}`]: value,
			}

			const type = updated['add-utility-type']
			const text = updated['add-utility-text']

			if (type && text) {
				const finalValue = `add-utility:${type}:${text}`

				if (multiple) {
					const cleaned = selectedValues.filter(
						(v) => !v.startsWith('add-utility:'),
					)
					;(props as MultiSelectProps).onChange([
						...cleaned,
						'add-utility',
						finalValue,
					])
				} else {
					;(props as SingleSelectProps).onChange(finalValue)
				}
			}

			return updated
		})
	}

	// ---------- render ----------
	return (
		<div ref={ref} className={`${className ?? ''} relative`}>
			<FloatingLabelField
				inputProps={{
					id,
					type: 'text',
					readOnly: true,
					value: selectedLabel,
					placeholder,
					onClick: () => setOpen((o) => !o),
					className: `placeholder:text-[#CCCCCC] placeholder:text-regular text-[14px] md:text-[16px] px-[33px] py-[24px] border rounded-[30px] ${errors?.filter(Boolean).length ? 'border-[#B33044]!' : ''}`,
				}}
				labelProps={labelProps}
				errors={errors}
				rightIcon={
					<ChevronDown
						className={`h-6 w-6 transition-transform ${
							open ? 'rotate-180' : ''
						}`}
					/>
				}
			/>

			{open && (
				<div className="absolute z-50 mt-2 w-full rounded-[20px] border bg-white shadow-lg">
					{dropdownHeader?.show && (
						<div className="border-b px-6 py-3">
							<div className="text-[14px] font-medium text-[#32302F]">
								{dropdownHeader.text}
							</div>

							{dropdownHeader.subText && (
								<div className="text-[12px] text-[#7A7A7A]">
									{dropdownHeader.subText}
								</div>
							)}
						</div>
					)}

					<div className="max-h-80 min-h-0 overflow-x-hidden overflow-y-auto">
						{options.map((option) => {
							const isSelected = selectedValues.includes(option.value)

							return (
								<div key={option.value}>
									<button
										type="button"
										onClick={() => handleSelect(option)}
										className="flex w-full items-center justify-between px-6 py-4 hover:bg-[#F6F6F6]"
									>
										<div className="flex w-[80%] items-center gap-3">
											{option.icon ? (
												<span className="flex h-5 w-5 items-center justify-center">
													{option.icon}
												</span>
											) : option.image ? (
												<img
													src={option.image}
													alt={option.label}
													className="h-5 w-5 object-contain"
												/>
											) : null}

											<span className="text-left">{option.label}</span>
										</div>
										{/* <span>{option.label}</span> */}
										<span
											className={`flex h-6 w-6 items-center justify-center rounded-full border ${
												isSelected ? 'border-[#32302F]' : 'border-[#CCCCCC]'
											}`}
										>
											{isSelected && (
												<span className="h-4 w-4 rounded-full bg-[#32302F]" />
											)}
										</span>
									</button>

									{/* allowInput */}
									{isSelected && option.allowInput && (
										<div className="px-6 pb-4">
											<input
												type="text"
												placeholder={option.inputPlaceholder ?? 'Required'}
												value={customInputs[option.value] ?? ''}
												onChange={(e) =>
													handleCustomInputChange(option.value, e.target.value)
												}
												className="w-full rounded-[10px] border px-3 py-2"
											/>
											{!customInputs[option.value]?.trim() && (
												<p className="mt-1 text-xs text-red-500">
													This field is required
												</p>
											)}
										</div>
									)}

									{/* DIVIDER */}
									{option.dividerAfter && (
										<div className="mx-6 border-t border-[#E5E5E5]" />
									)}

									{/* add-utility */}
									{option.value === 'add-utility' && isSelected && (
										<div className="flex gap-2 px-6 pb-4">
											<select
												className="flex-1 rounded-[10px] border px-3 py-2"
												value={customInputs['add-utility-type'] ?? ''}
												onChange={(e) =>
													handleAddUtilityChange('type', e.target.value)
												}
											>
												<option value="">Select</option>
												<option value="tenant">Tenant</option>
												<option value="owner">Owner</option>
											</select>

											<input
												type="text"
												placeholder="Add Utilities"
												className="flex-1 rounded-[10px] border px-3 py-2"
												value={customInputs['add-utility-text'] ?? ''}
												onChange={(e) =>
													handleAddUtilityChange('text', e.target.value)
												}
											/>
										</div>
									)}
								</div>
							)
						})}
					</div>
				</div>
			)}
		</div>
	)
}

export function FloatingComboboxField({
	labelProps,
	value,
	onChange,
	options,
	errors,
	className,
	errorClass: _errorClass,
	placeholder = 'Type or select',
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	value?: string
	onChange: (value: string) => void
	options: Option[]
	errors?: ListOfErrors
	className?: string
	errorClass?: string
	placeholder?: string
}) {
	const id = useId()
	const listId = `${id}-list`
	const [isOpen, setIsOpen] = useState(false)
	const [filteredOptions, setFilteredOptions] = useState(options)
	const dropdownRef = useRef<HTMLDivElement>(null)

	// Filter options based on input value
	useEffect(() => {
		if (value) {
			const filtered = options.filter((option) =>
				option.label.toLowerCase().includes(value.toLowerCase()),
			)
			setFilteredOptions(filtered.length > 0 ? filtered : options)
		} else {
			setFilteredOptions(options)
		}
	}, [value, options])

	// Close dropdown on outside click
	useEffect(() => {
		function handleClickOutside(e: MouseEvent) {
			const inputElement = document.getElementById(
				id,
			) as HTMLInputElement | null
			if (
				dropdownRef.current &&
				inputElement &&
				!dropdownRef.current.contains(e.target as Node) &&
				!inputElement.contains(e.target as Node)
			) {
				setIsOpen(false)
			}
		}
		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside)
		}
		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [isOpen, id])

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = e.target.value
		onChange(newValue)
		setIsOpen(true)
	}

	const handleOptionSelect = (optionValue: string, optionLabel: string) => {
		onChange(optionLabel)
		setIsOpen(false)
		const inputElement = document.getElementById(id) as HTMLInputElement | null
		inputElement?.blur()
	}

	const handleInputFocus = () => {
		setIsOpen(true)
	}

	return (
		<div ref={dropdownRef} className={`${className || ''} relative`}>
			<FloatingLabelField
				inputProps={{
					id,
					type: 'text',
					placeholder: placeholder,
					value: value || '',
					onChange: handleInputChange,
					onFocus: handleInputFocus,
					list: listId,
					className: `placeholder:text-[#CCCCCC] placeholder:text-[16px] placeholder:text-regular text-[16px] px-[33px] py-[24px] text-[#484848] border rounded-[30px] ${
						errors?.length ? 'border-[#B33044]!' : 'border-[#CCCCCC]'
					}`,
					'aria-invalid': errors?.length ? true : undefined,
					'aria-describedby': errors?.length ? `${id}-error` : undefined,
					'aria-autocomplete': 'list',
					'aria-expanded': isOpen,
					'aria-controls': isOpen ? listId : undefined,
				}}
				labelProps={labelProps}
				errors={errors}
				rightIcon={
					<ChevronDown
						className={`h-6 w-6 cursor-pointer text-[#32302F] transition-transform ${
							isOpen ? 'rotate-180' : ''
						}`}
						onClick={() => setIsOpen(!isOpen)}
					/>
				}
			/>

			{/* Dropdown suggestions */}
			{isOpen && filteredOptions.length > 0 && (
				<div
					id={listId}
					// biome-ignore lint/a11y/useSemanticElements: Custom dropdown requires div with ARIA for styling
					role="listbox"
					tabIndex={-1}
					aria-label="Options"
					className="absolute z-50 mt-2 max-h-60 w-full overflow-y-auto rounded-[20px] border border-[#E5E5E5] bg-white shadow-lg"
				>
					{filteredOptions.map((option) => {
						const isSelected = option.label === value
						return (
							<button
								key={option.value}
								type="button"
								// biome-ignore lint/a11y/useSemanticElements: Custom dropdown requires button with ARIA for styling
								role="option"
								aria-selected={isSelected}
								onClick={() => handleOptionSelect(option.value, option.label)}
								className="flex w-full items-center justify-between px-6 py-4 text-left text-[16px] text-[#32302F] transition-colors first:rounded-t-[20px] last:rounded-b-[20px] hover:bg-[#F6F6F6]"
							>
								<span>{option.label}</span>
								{isSelected && (
									<span className="h-4 w-4 rounded-full bg-[#32302F]" />
								)}
							</button>
						)
					})}
				</div>
			)}
		</div>
	)
}

export function FloatingCalendarField({
	labelProps,
	value,
	onChange,
	errors,
	className,
	errorClass,
	placeholder = 'Select Move-In Date',
	CalendarComponent,
	flexible,
	onFlexibleChange,
	minDate,
	maxDate,
	showFlexibleToggle,
	compact,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	value?: Date
	onChange: (date: Date | undefined) => void
	errors?: ListOfErrors
	className?: string
	errorClass?: string
	placeholder?: string
	minDate?: Date
	maxDate?: Date
	CalendarComponent: React.ComponentType<{
		value?: Date
		onChange: (date: Date) => void
		triggerRef?: React.RefObject<HTMLElement | null>
		flexible?: boolean
		onFlexibleChange?: (flexible: boolean) => void
		minDate?: Date
		maxDate?: Date
		showFlexibleToggle?: boolean
		compact?: boolean
	}>
	flexible?: boolean
	onFlexibleChange?: (flexible: boolean) => void
	showFlexibleToggle?: boolean
	/** Forwarded to the calendar — shrink to base size for narrow hosts. */
	compact?: boolean
}) {
	const fallbackId = useId()
	const id = labelProps.htmlFor || fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined

	const inputRef = useRef<HTMLInputElement>(null)
	const wrapperRef = useRef<HTMLDivElement>(null)
	const calendarRef = useRef<HTMLDivElement>(null)
	const [isOpen, setIsOpen] = useState(false)
	const [isFocused, setIsFocused] = useState(false)

	const hasValue = Boolean(value)

	// Format date for display
	const displayValue = value ? format(value, 'MMMM d, yyyy') : ''

	// Close calendar on outside click
	useEffect(() => {
		function handleClickOutside(e: MouseEvent) {
			if (!isOpen) return

			const target = e.target as Node

			if (
				inputRef.current?.contains(target) ||
				calendarRef.current?.contains(target) ||
				// The calendar renders in a body portal (fixed popover), so it's
				// outside calendarRef's subtree — match its marker instead.
				(target instanceof Element && target.closest('[data-calendar-popover]'))
			) {
				return
			}

			setIsOpen(false)
			setIsFocused(false)
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [isOpen])

	const handleFocus = () => {
		setIsFocused(true)
	}

	const handleBlur = () => {
		// Delay to allow calendar click to register
		setTimeout(() => {
			setIsFocused(false)
		}, 200)
	}

	const handleInputClick = () => {
		setIsOpen(true)
		setIsFocused(true)
	}

	const handleDateChange = (date: Date) => {
		onChange(date)
		setIsOpen(false)
		setIsFocused(false)
	}

	return (
		<div ref={wrapperRef} className={cn('relative', className)}>
			{flexible && hasValue && (
				<style>{`input.floating-label-input-${id}{color:transparent!important;-webkit-text-fill-color:transparent!important;}`}</style>
			)}
			<FloatingLabelField
				inputProps={{
					id,
					type: 'text',
					readOnly: true,
					// FloatingLabelField already gates the placeholder (label at rest
					// acts as the placeholder; when there's no label it shows on load),
					// so pass it straight through.
					placeholder,
					value: displayValue,
					onClick: handleInputClick,
					onFocus: handleFocus,
					onBlur: handleBlur,
					className: cn(
						'placeholder:text-regular w-full min-w-0 cursor-pointer rounded-[15px] border border-[#CCCCCC] bg-[#FBFDFD] text-[14px] text-[#484848] placeholder:text-[16px] placeholder:text-[#CCCCCC] md:text-[16px]',
						compact ? 'px-4 py-3' : 'px-[33px] py-[24px]',
					),
					'aria-invalid': errorId ? true : undefined,
					'aria-describedby': errorId,
				}}
				labelProps={labelProps}
				errors={errors}
				errorClass={errorClass}
				rightIcon={
					<img
						src="/img/calender.png"
						alt="calendar"
						className="h-6 w-6 text-[#32302F]"
					/>
				}
			/>

			{/* Calendar */}
			{isOpen && (
				<div ref={calendarRef}>
					<CalendarComponent
						value={value}
						onChange={handleDateChange}
						triggerRef={wrapperRef}
						flexible={flexible}
						onFlexibleChange={onFlexibleChange}
						minDate={minDate}
						maxDate={maxDate}
						showFlexibleToggle={showFlexibleToggle}
						compact={compact}
					/>
				</div>
			)}

			{/* Flexible badge overlay — vertically centred over the field (inset-y-0
				    + items-center) so the date lines up with the input, with the
				    "Flexible" pill inline after it. left-[33px] matches the input's
				    padding; right-[52px] leaves room for the calendar icon. The input
				    text itself is hidden (text-transparent) above; pointer-events-none
				    keeps clicks flowing through to the input. */}
			{flexible && hasValue && (
				<div className="pointer-events-none absolute inset-y-0 right-[52px] left-[33px] flex items-center gap-3">
					<span className="text-[14px] text-[#484848] md:text-[16px]">
						{displayValue}
					</span>
					<span className="inline-flex items-center rounded-full bg-[#32302F] px-3 py-1 text-[12px] leading-none font-medium text-white">
						Flexible
					</span>
				</div>
			)}
		</div>
	)
}

// Helper function to parse years/months from formatted string
function parseYearsMonths(value: string): { years: number; months: number } {
	if (!value || !value.trim()) {
		return { years: 0, months: 0 }
	}

	const trimmed = value.trim().toLowerCase()

	// Handle "X years & Y months" or "X years Y months" format
	const yearsMonthsMatch = trimmed.match(
		/(\d+)\s*years?\s*&?\s*(\d+)\s*months?/,
	)
	if (yearsMonthsMatch) {
		return {
			years: Number.parseInt(yearsMonthsMatch[1], 10) || 0,
			months: Number.parseInt(yearsMonthsMatch[2], 10) || 0,
		}
	}

	// Handle "X years" format
	const yearsMatch = trimmed.match(/(\d+)\s*years?/)
	if (yearsMatch) {
		return {
			years: Number.parseInt(yearsMatch[1], 10) || 0,
			months: 0,
		}
	}

	// Handle "X months" format
	const monthsMatch = trimmed.match(/(\d+)\s*months?/)
	if (monthsMatch) {
		const totalMonths = Number.parseInt(monthsMatch[1], 10) || 0
		return {
			years: Math.floor(totalMonths / 12),
			months: totalMonths % 12,
		}
	}

	// Handle decimal format (e.g., "2.5" = 2 years 6 months)
	const decimalMatch = trimmed.match(/^(\d+\.?\d*)$/)
	if (decimalMatch) {
		const decimal = Number.parseFloat(decimalMatch[1])
		if (!Number.isNaN(decimal)) {
			const years = Math.floor(decimal)
			const months = Math.round((decimal - years) * 12)
			return { years, months }
		}
	}

	// Handle plain number (assume years)
	const numberMatch = trimmed.match(/^(\d+)$/)
	if (numberMatch) {
		return {
			years: Number.parseInt(numberMatch[1], 10) || 0,
			months: 0,
		}
	}

	return { years: 0, months: 0 }
}

// Helper function to format years/months as string
function formatYearsMonths(years: number, months: number): string {
	const y = Math.max(0, Math.floor(years))
	const m = Math.max(0, Math.min(11, Math.floor(months)))

	if (y === 0 && m === 0) {
		return ''
	}

	const parts: string[] = []
	if (y > 0) {
		parts.push(`${y} ${y === 1 ? 'Year' : 'Years'}`)
	}
	if (m > 0) {
		parts.push(`${m} ${m === 1 ? 'Month' : 'Months'}`)
	}

	return parts.join(' & ')
}

/**
 * Whole-months duration between two dates, formatted as the same
 * "X years & Y months" string the duration picker produces — so it can be
 * stored in the existing duration fields (previousDurationLived,
 * previousYearsOfEmployment) that reports/AI read by name. Returns '' when
 * either date is missing, the range is inverted, or it's under a month.
 */
export function durationBetweenDates(
	start: Date | null | undefined,
	end: Date | null | undefined,
): string {
	if (!start || !end) return ''
	if (end < start) return ''
	let months =
		(end.getFullYear() - start.getFullYear()) * 12 +
		(end.getMonth() - start.getMonth())
	// Not a full month yet if the end day-of-month hasn't reached the start's.
	if (end.getDate() < start.getDate()) months -= 1
	if (months < 0) months = 0
	return formatYearsMonths(Math.floor(months / 12), months % 12)
}

/**
 * Combined move-in / move-out (or start / end) date-range field. Renders one
 * labelled input showing "01 Jan 2020 – 15 Jun 2022" and opens the shared
 * single-date calendar one step at a time: move-in first, then move-out
 * (constrained to ≥ move-in). Mirrors FloatingCalendarField's look + behaviour.
 */
export function FloatingDateRangeField({
	labelProps,
	startValue,
	endValue,
	onChange,
	errors,
	className,
	errorClass,
	placeholder = 'Select Start & End Date',
	maxDate,
	minDate,
	dateFormat = 'MMMM d, yyyy',
	CalendarComponent,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	startValue?: Date
	endValue?: Date
	onChange: (start: Date | undefined, end: Date | undefined) => void
	errors?: ListOfErrors
	className?: string
	errorClass?: string
	placeholder?: string
	maxDate?: Date
	minDate?: Date
	/** date-fns format for the read-only display, e.g. "MMMM yyyy" for a
	 * month/year range picker. Defaults to the full date. */
	dateFormat?: string
	CalendarComponent: React.ComponentType<{
		startValue?: Date
		endValue?: Date
		onChange: (start: Date | undefined, end: Date | undefined) => void
		triggerRef?: React.RefObject<HTMLElement | null>
		minDate?: Date
		maxDate?: Date
	}>
}) {
	const fallbackId = useId()
	const id = labelProps.htmlFor || fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined

	const inputRef = useRef<HTMLInputElement>(null)
	const wrapperRef = useRef<HTMLDivElement>(null)
	const calendarRef = useRef<HTMLDivElement>(null)
	const [isOpen, setIsOpen] = useState(false)
	const [isFocused, setIsFocused] = useState(false)

	const hasValue = Boolean(startValue || endValue)
	const shouldFloat = isFocused || hasValue

	const displayValue = (() => {
		const s = startValue ? format(startValue, dateFormat) : ''
		const e = endValue ? format(endValue, dateFormat) : ''
		if (s && e) return `${s} – ${e}`
		if (s) return `${s} – Select end date`
		return ''
	})()

	// Close on outside click (mirrors FloatingCalendarField).
	useEffect(() => {
		function handleClickOutside(ev: MouseEvent) {
			if (!isOpen) return
			const target = ev.target as Node
			if (
				inputRef.current?.contains(target) ||
				calendarRef.current?.contains(target) ||
				(target instanceof Element &&
					target.closest('.month-year-range-picker'))
			) {
				return
			}
			setIsOpen(false)
			setIsFocused(false)
		}
		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [isOpen])

	const handleInputClick = () => {
		setIsFocused(true)
		setIsOpen(true)
	}

	const handleRangeChange = (
		start: Date | undefined,
		end: Date | undefined,
	) => {
		onChange(start, end)
		// Close once a full range is chosen; stay open while only the start is set.
		if (start && end) {
			setIsOpen(false)
			setIsFocused(false)
		}
	}

	return (
		<div ref={wrapperRef} className={cn('relative', className)}>
			<FloatingLabelField
				inputProps={{
					id,
					type: 'text',
					readOnly: true,
					placeholder: shouldFloat ? placeholder : '',
					value: displayValue,
					onClick: handleInputClick,
					onFocus: () => setIsFocused(true),
					onBlur: () => setTimeout(() => setIsFocused(false), 200),
					className:
						'placeholder:text-[#CCCCCC] placeholder:text-[16px] placeholder:text-regular text-[14px] md:text-[16px] px-[33px] py-[24px] text-[#484848] border border-[#CCCCCC] rounded-[15px] bg-[#FBFDFD] cursor-pointer',
					'aria-invalid': errorId ? true : undefined,
					'aria-describedby': errorId,
				}}
				labelProps={labelProps}
				errors={errors}
				errorClass={errorClass}
				rightIcon={
					<img
						src="/img/calender.png"
						alt="calendar"
						className="h-6 w-6 text-[#32302F]"
					/>
				}
			/>

			{isOpen && (
				<div ref={calendarRef}>
					<CalendarComponent
						startValue={startValue}
						endValue={endValue}
						onChange={handleRangeChange}
						triggerRef={wrapperRef}
						minDate={minDate}
						maxDate={maxDate}
					/>
				</div>
			)}
		</div>
	)
}

// DurationPicker popup component
function DurationPicker({
	value,
	onChange,
	triggerRef,
	maxYears = 100,
	maxMonths = 11,
	overflowYearsLabel,
	title = 'Select Duration',
}: {
	value?: string
	onChange: (value: string) => void
	triggerRef?: React.RefObject<HTMLElement | null>
	maxYears?: number
	maxMonths?: number
	/** When set (e.g. "35+ years"), adds one option after maxYears; stored value is (maxYears+1) years */
	overflowYearsLabel?: string
	/** Heading shown above the Years / Months columns. */
	title?: string
}) {
	const pickerRef = useRef<HTMLDivElement>(null)
	const yearsRef = useRef<HTMLDivElement>(null)
	const monthsRef = useRef<HTMLDivElement>(null)
	const [position, setPosition] = useState<'top' | 'bottom'>('bottom')
	const [selectedYears, setSelectedYears] = useState(0)
	const [selectedMonths, setSelectedMonths] = useState(0)

	// Parse initial value; when overflow (35+ years) is stored, force 0 months
	const overflowYearValueNum = overflowYearsLabel ? maxYears + 1 : null
	useEffect(() => {
		if (value) {
			const parsed = parseYearsMonths(value)
			setSelectedYears(parsed.years)
			setSelectedMonths(
				overflowYearValueNum !== null && parsed.years >= overflowYearValueNum
					? 0
					: parsed.months,
			)
		} else {
			setSelectedYears(0)
			setSelectedMonths(0)
		}
	}, [value, overflowYearValueNum])

	// Calculate position based on available space
	useEffect(() => {
		const calculatePosition = () => {
			if (triggerRef?.current) {
				const rect = triggerRef.current.getBoundingClientRect()
				const viewportHeight = window.innerHeight
				const spaceBelow = viewportHeight - rect.bottom
				const spaceAbove = rect.top

				// Duration picker height is approximately 400px
				const pickerHeight = 400

				// Position above if there's more space above AND not enough space below
				// Otherwise position below (default)
				if (spaceBelow < pickerHeight && spaceAbove > spaceBelow) {
					setPosition('top')
				} else {
					setPosition('bottom')
				}
			}
		}

		// Calculate immediately when component renders
		calculatePosition()

		// Small delay to ensure DOM is ready
		const timer = setTimeout(calculatePosition, 10)

		// Recalculate on scroll and resize
		window.addEventListener('scroll', calculatePosition, true)
		window.addEventListener('resize', calculatePosition)

		return () => {
			clearTimeout(timer)
			window.removeEventListener('scroll', calculatePosition, true)
			window.removeEventListener('resize', calculatePosition)
		}
	}, [triggerRef])

	// Scroll to selected values when component opens
	useEffect(() => {
		if (yearsRef.current && selectedYears >= 0) {
			const yearElement = yearsRef.current.querySelector(
				`[data-year="${selectedYears}"]`,
			) as HTMLElement
			if (yearElement) {
				yearElement.scrollIntoView({ block: 'center', behavior: 'smooth' })
			}
		}
		if (monthsRef.current && selectedMonths >= 0) {
			const monthElement = monthsRef.current.querySelector(
				`[data-month="${selectedMonths}"]`,
			) as HTMLElement
			if (monthElement) {
				monthElement.scrollIntoView({ block: 'center', behavior: 'smooth' })
			}
		}
	}, [selectedYears, selectedMonths])

	const handleConfirm = () => {
		const formatted = formatYearsMonths(selectedYears, selectedMonths)
		onChange(formatted)
	}

	const overflowYearValue = overflowYearsLabel ? maxYears + 1 : null
	const selectedDisplay =
		overflowYearsLabel &&
		overflowYearValue !== null &&
		selectedYears >= overflowYearValue
			? overflowYearsLabel
			: formatYearsMonths(selectedYears, selectedMonths)

	// Generate years and months arrays; with overflow, years are 0..maxYears plus one "overflow" option
	const years: number[] = overflowYearsLabel
		? [...Array.from({ length: maxYears + 1 }, (_, i) => i), maxYears + 1]
		: Array.from({ length: maxYears + 1 }, (_, i) => i)
	const months = Array.from({ length: maxMonths + 1 }, (_, i) => i)

	const getPositionStyles = () => {
		if (!triggerRef?.current) return {}
		const rect = triggerRef.current.getBoundingClientRect()
		return {
			position: 'fixed' as const,
			left: `${rect.left}px`,
			width: `${rect.width}px`,
			...(position === 'top'
				? { bottom: `${window.innerHeight - rect.top + 8}px` }
				: { top: `${rect.bottom + 8}px` }),
			zIndex: 50,
		}
	}

	return (
		<div
			ref={pickerRef}
			className="absolute z-50 w-full"
			style={getPositionStyles()}
		>
			<div className="w-full rounded-2xl border border-gray-200 bg-white p-4 shadow-lg">
				{/* Header */}
				<div className="mb-2 text-start">
					<h3 className="font-regular text-md text-gray-800">{title}</h3>
				</div>

				{/* Column Headers */}
				<div
					className={`grid gap-4 rounded-tl-[10px] rounded-tr-[10px] border-b border-gray-200 bg-[#32302F] px-3 py-3 ${
						overflowYearsLabel &&
						overflowYearValue !== null &&
						selectedYears >= overflowYearValue
							? 'grid-cols-1'
							: 'grid-cols-2'
					}`}
				>
					<div className="text-left font-medium text-[#FFFFFF]">Years</div>
					{!(
						overflowYearsLabel &&
						overflowYearValue !== null &&
						selectedYears >= overflowYearValue
					) && (
						<div className="text-left font-medium text-[#FFFFFF]">Months</div>
					)}
				</div>

				{/* Scrollable Columns */}
				<div
					className={`grid h-[250px] gap-2 overflow-hidden rounded-br-[10px] rounded-bl-[10px] border border-[#F3F3F3] ${
						overflowYearsLabel &&
						overflowYearValue !== null &&
						selectedYears >= overflowYearValue
							? 'grid-cols-1'
							: 'grid-cols-2'
					}`}
				>
					{/* Years Column */}
					<div
						ref={yearsRef}
						className="scrollbar-thumb-[#32302F] scrollbar-track-[#F3F3F3] overflow-y-auto transition-colors [&::-webkit-scrollbar]:w-[5px] [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-[#4a4847]"
					>
						{years.map((year) => (
							<button
								key={year}
								type="button"
								data-year={year}
								onClick={() => {
									setSelectedYears(year)
									if (
										overflowYearsLabel &&
										overflowYearValue !== null &&
										year === overflowYearValue
									) {
										setSelectedMonths(0)
									}
								}}
								className={`w-full px-3 py-2 text-left text-[14px] transition-colors ${
									selectedYears === year
										? 'bg-[#F3F3F3] font-medium text-gray-900'
										: 'text-gray-700 hover:bg-gray-100'
								}`}
							>
								{overflowYearsLabel &&
								overflowYearValue !== null &&
								year === overflowYearValue
									? overflowYearsLabel
									: `${year} ${year === 1 ? 'Year' : 'Years'}`}
							</button>
						))}
					</div>

					{/* Months Column - hidden when overflow (35+ years) is selected */}
					{!(
						overflowYearsLabel &&
						overflowYearValue !== null &&
						selectedYears >= overflowYearValue
					) && (
						<div
							ref={monthsRef}
							className="scrollbar-thumb-[#32302F] scrollbar-track-[#F3F3F3] overflow-y-auto transition-colors [&::-webkit-scrollbar]:w-[5px] [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-[#4a4847]"
						>
							{months.map((month) => (
								<button
									key={month}
									type="button"
									data-month={month}
									onClick={() => setSelectedMonths(month)}
									className={`w-full px-3 py-2 text-left text-[14px] transition-colors ${
										selectedMonths === month
											? 'bg-[#F3F3F3] font-medium text-gray-900'
											: 'text-gray-700 hover:bg-gray-100'
									}`}
								>
									{month} {month === 1 ? 'Month' : 'Months'}
								</button>
							))}
						</div>
					)}
				</div>

				{/* Footer */}
				<div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
					<div className="text-sm text-gray-600">
						Total Duration : {selectedDisplay || '0 Years 0 Months'}
					</div>
					<button
						type="button"
						onClick={handleConfirm}
						className="cursor-pointer rounded-full bg-[#32302f] px-6 py-2 font-medium text-white transition-colors hover:bg-black"
					>
						Confirm
					</button>
				</div>
			</div>
		</div>
	)
}

export function FloatingDurationField({
	labelProps,
	value,
	onChange,
	errors,
	className,
	errorClass,
	placeholder = 'Select Years/Months',
	maxYears = 100,
	maxMonths = 11,
	overflowYearsLabel,
	title,
}: {
	labelProps: React.LabelHTMLAttributes<HTMLLabelElement>
	value?: string
	onChange: (value: string) => void
	errors?: ListOfErrors
	className?: string
	errorClass?: string
	placeholder?: string
	maxYears?: number
	maxMonths?: number
	/** When set (e.g. "35+ years"), years list is 0..maxYears plus this option */
	overflowYearsLabel?: string
	/** Heading shown in the picker; defaults to "Select Duration". */
	title?: string
}) {
	const fallbackId = useId()
	const id = labelProps.htmlFor || fallbackId
	const errorId = errors?.length ? `${id}-error` : undefined

	const inputRef = useRef<HTMLInputElement>(null)
	const pickerRef = useRef<HTMLDivElement>(null)
	const [isOpen, setIsOpen] = useState(false)
	const [isFocused, setIsFocused] = useState(false)

	const hasValue = Boolean(value?.trim())
	const shouldFloat = isFocused || hasValue

	// Show overflow label (e.g. "35+ years") in the input when stored value is that option
	const displayValue =
		value?.trim() && overflowYearsLabel
			? (() => {
					const parsed = parseYearsMonths(value)
					if (parsed.years === maxYears + 1 && parsed.months === 0) {
						return overflowYearsLabel
					}
					return value
				})()
			: (value ?? '')

	// Close picker on outside click
	useEffect(() => {
		function handleClickOutside(e: MouseEvent) {
			if (!isOpen) return

			const target = e.target as Node

			if (
				inputRef.current?.contains(target) ||
				pickerRef.current?.contains(target)
			) {
				return
			}

			setIsOpen(false)
			setIsFocused(false)
		}

		document.addEventListener('mousedown', handleClickOutside)
		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [isOpen])

	const handleFocus = () => {
		setIsFocused(true)
	}

	const handleBlur = () => {
		// Delay to allow picker click to register
		setTimeout(() => {
			setIsFocused(false)
		}, 200)
	}

	const handleInputClick = () => {
		setIsOpen(true)
		setIsFocused(true)
	}

	const handleDurationChange = (newValue: string) => {
		onChange(newValue)
		setIsOpen(false)
		setIsFocused(false)
	}

	return (
		<div className={cn('relative', className)}>
			<FloatingLabelField
				inputProps={{
					id,
					type: 'text',
					readOnly: true,
					placeholder: shouldFloat ? placeholder : '',
					value: displayValue || '',
					onClick: handleInputClick,
					onFocus: handleFocus,
					onBlur: handleBlur,
					className:
						'placeholder:text-[#CCCCCC] placeholder:text-[16px] placeholder:text-regular text-[16px] px-[33px] py-[24px] text-[#484848] border border-[#CCCCCC] rounded-[15px] bg-[#FBFDFD] cursor-pointer',
					'aria-invalid': errorId ? true : undefined,
					'aria-describedby': errorId,
				}}
				labelProps={labelProps}
				errors={errors}
				errorClass={errorClass}
				rightIcon={
					<img
						src="/img/calender.png"
						alt="duration"
						className="h-6 w-6 text-[#32302F]"
					/>
				}
			/>

			{/* Duration Picker */}
			{isOpen && (
				<div ref={pickerRef} className="d-block w-full">
					<DurationPicker
						value={value}
						onChange={handleDurationChange}
						triggerRef={inputRef}
						maxYears={maxYears}
						maxMonths={maxMonths}
						overflowYearsLabel={overflowYearsLabel}
						title={title}
					/>
				</div>
			)}
		</div>
	)
}
